import { computed, observable } from 'mobx';
import {
    ServiceSelectionType,
    ServicesItemsLayoutType
} from '~/types/model.types';
import { CXContainerableBlock } from '~/components/ClientExperience/OneFlow/models/cx-containerable-block.model';
import { IServiceSelectionBlockStyles } from '~/types/block-styles.types';
import type { CXServiceItemsContainerBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-items-container-block.model';
import {
    IServiceSelectionBlockResponse,
    TServiceSelectionBlockType
} from '~/types/server.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { ISelectedServiceValue } from '~/components/ClientExperience/OneFlow/FlowContent/CXServiceSelectionBlock/CXServiceSelection.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { CXServiceSelectionItemBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-selection-item-block.model';

export class CXServiceSelectionBlock extends CXContainerableBlock {
    type: 'service_selection_block';
    @observable selection_required: boolean;
    @observable selection_type: ServiceSelectionType;
    @observable service_selection_type: TServiceSelectionBlockType;
    @observable show_total: boolean;
    @observable discount: boolean;
    @observable services_layout: ServicesItemsLayoutType;
    @observable banner_text: string;
    @observable styles: IServiceSelectionBlockStyles;
    @observable invoice_block_id: string;
    @observable send_info_to_invoice: boolean;

    constructor(
        block: IServiceSelectionBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
        this.connected_variable_id = block.connected_variable_id;
        this.service_selection_type = block.service_selection_type;
    }

    @computed
    get isActionBlockForPrint() {
        return true;
    }

    get displayName() {
        return 'services';
    }

    @computed
    get isPendingClientCompletion() {
        return !this.isSelectionComplete;
    }

    @computed
    get serviceItems(): CXServiceSelectionItemBlock[] {
        return this.childBlocks.reduce((serviceItems, childBlock) => {
            if (childBlock.type === 'service_items_container_block') {
                serviceItems.push(
                    ...(childBlock as CXServiceItemsContainerBlock).childBlocks
                );
            }
            return serviceItems;
        }, []);
    }

    @computed
    get hasSelection() {
        return this.selection_type !== ServiceSelectionType.NoSelection;
    }

    @computed
    get isChargeable() {
        return this.hasSelection || this.send_info_to_invoice;
    }

    @computed
    get isSelectionComplete() {
        if (
            this.selection_type === ServiceSelectionType.NoSelection ||
            !this.selection_required
        ) {
            return true;
        }
        return this.selectedServiceBlocks.length > 0;
    }

    @computed
    get selectedServiceBlocks() {
        if (!this.isChargeable) {
            return [];
        }

        if (!this.hasSelection && this.send_info_to_invoice) {
            return this.serviceItems;
        }

        const selectedItemsIds = (
            (this.connectedVariable?.value
                ?.selected_services as ISelectedServiceValue[]) || []
        ).map(item => item.service_item_block_id);
        return this.serviceItems.filter(item =>
            selectedItemsIds.includes(item.block_id)
        );
    }

    @computed
    get isColumnLayout() {
        return this.services_layout !== ServicesItemsLayoutType.Rows;
    }

    @computed
    get variableValue() {
        return this.rootStore.flowStore.variables[this.connected_variable_id]
            ?.value;
    }

    @computed
    get variableRawValue() {
        // todo delete this when getting rid of raw_value completely
        return this.rootStore.flowStore.variables[this.connected_variable_id]
            ?.value;
    }

    @computed
    get isSessionSelection() {
        return this.service_selection_type === 'session';
    }

    @computed
    get firstSelectedService(): CXServiceSelectionItemBlock {
        const selected_services =
            this.connectedVariable?.value?.selected_services;

        if (selected_services && selected_services.length > 0) {
            return this.serviceItems.find(
                item =>
                    item.block_id === selected_services[0].service_item_block_id
            );
        }
    }
}
